import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  descend,
  find,
  flatten,
  isEmpty,
  pickBy,
  pluck,
  prop,
  propEq,
  sortWith,
  uniqBy,
} from 'ramda';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Graphic from 'components/Graphic/Graphic';
import { FilterType, SearchFilter } from 'components/SearchFilter/SearchFilter';
import { motion } from 'framer-motion';
import { patientsQuery } from 'components/Layout/Layout';
import Icon from '../../components/Icon/Icon';
import Patient from '../../components/Patient/Patient';
import {
  getPatientsQuestionnaires,
  sortPatientsVisitsByMonthYear,
  useAppContext,
} from '../../context/appContext';
import {
  VISIT_STATUS,
  VISIT_TYPE_CONSTANTS,
  visitIconsAndColors,
  visitTypes,
} from '../../context/visitContext';
import { QuestionAnswers, Patient as PatientType } from '../../types/types';
import {
  formatPatientAgeAndGender,
  hasOpenedFollowUp,
  hasOpenedLabReport,
  isRole,
  visitNotificationEvents,
  latestDatedEvent,
  visitDateStamp,
  formatStampDate,
  sessionTStorage,
  TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY,
  US_STATES,
  VisitNotificationEvent,
} from '../../utils/utils';
import { paths } from '../../routes';
import { getOpenedVisits, markVisitOpened, isVisitNewToProvider } from '../../utils/openedVisits';

const EVENT_STYLES: Record<VisitNotificationEvent, { label: string; dot: string; tint: string }> = {
  lab: { label: 'Lab Results', dot: '#C2318F', tint: '#FBEAF4' },
  chat: { label: 'New Message', dot: '#1B87C9', tint: '#E4F3FC' },
  followUp: { label: 'Follow-Up', dot: '#1B8F55', tint: '#E3F4EB' },
};

const RESTING_DISC = { dot: '#F6914F', tint: '#FEEEE2' };

const patientsQuestionnairesQuery = (patients: PatientType[], userId?: string) => ({
  queryKey: ['patients-questionnaires', patients.length, userId], // we want to refresh when patient data changes
  queryFn: () => getPatientsQuestionnaires(patients, userId),
});

const PATIENTS_LIMIT = 5;

function Home() {
  const [{ user, patients, unReadMessages: unReadAnsweredQnAs }, dispatch] = useAppContext();
  const [patientsLimit, setPatientsLimit] = React.useState(PATIENTS_LIMIT);
  const [filteredPatients, setFilteredPatients] = React.useState<PatientType[]>();
  const [expandedSearch, setExpandedSearch] = React.useState<Record<string, string>>();
  const [searchTypingTimer, setSearchTypingTimer] = React.useState<NodeJS.Timeout | undefined>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const doneSearchTypingInterval = 500;

  const { data, isLoading, isFetching } = useQuery({
    ...patientsQuestionnairesQuery(patients, user?.id),
    enabled: !!(patients.length && user?.id),
    staleTime: 1000 * 50,
  });

  const advancedSearch = useQuery({
    ...patientsQuery(user, expandedSearch),
    queryKey: ['filtered-patients', expandedSearch],
    enabled: !!expandedSearch?.q,
  });

  const queryParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const hasUnReadAnsweredQnAs = React.useCallback(
    (visit: QuestionAnswers) => {
      return Boolean(
        unReadAnsweredQnAs?.find(fndUnReadQnAs => fndUnReadQnAs?.visitId === visit.id),
      );
    },
    [unReadAnsweredQnAs],
  );

  const getVisitEvents = React.useCallback(
    (visit: QuestionAnswers) => visitNotificationEvents(visit, hasUnReadAnsweredQnAs(visit)),
    [hasUnReadAnsweredQnAs],
  );

  const [patientsWithVisits, visits] = React.useMemo(() => {
    const fndPatientsVisits = data || [patients, sortPatientsVisitsByMonthYear(patients)] || [];

    let fndPendingVisits = flatten(pluck('questionnaires', fndPatientsVisits[0])).filter(
      fndQuestion => {
        if (!isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')) {
          const isPending = fndQuestion.status === VISIT_STATUS.UNANSWERED;
          return isPending || (fndQuestion.labTests && !hasOpenedLabReport(fndQuestion.labTests));
        }
        return fndQuestion.status === VISIT_STATUS.COMPLETE;
      },
    );

    fndPendingVisits = sortWith(
      [
        descend((fndQuestion: QuestionAnswers) => getVisitEvents(fndQuestion).length > 0),
        descend(
          (fndQuestion: QuestionAnswers) =>
            visitDateStamp(fndQuestion, hasUnReadAnsweredQnAs(fndQuestion))?.at ||
            fndQuestion.updatedAt ||
            fndQuestion.createdAt,
        ),
      ],
      fndPendingVisits,
    );

    return [fndPatientsVisits[0], fndPendingVisits];
  }, [data, patients, user?.roles, user?.id, hasUnReadAnsweredQnAs, getVisitEvents]);

  const [openedVisitIds, setOpenedVisitIds] = React.useState<Set<string>>(() =>
    getOpenedVisits(user?.id),
  );

  React.useEffect(() => {
    setOpenedVisitIds(getOpenedVisits(user?.id));
  }, [user?.id]);

  const getPrimaryEvent = (visit: QuestionAnswers) =>
    latestDatedEvent(visit, hasUnReadAnsweredQnAs(visit))?.event || getVisitEvents(visit)[0];

  const getDiscStyle = (visit: QuestionAnswers) => {
    const primary = getPrimaryEvent(visit);
    return primary ? EVENT_STYLES[primary] : RESTING_DISC;
  };

  const isRowBold = (visit: QuestionAnswers) => {
    if (getVisitEvents(visit).length > 0) return true;

    return !openedVisitIds.has(visit.id) && isVisitNewToProvider(visit, user?.id);
  };

  const rememberVisitOpened = (visit: QuestionAnswers) => {
    markVisitOpened(visit.id, user?.id);
    setOpenedVisitIds(previous => new Set(previous).add(visit.id));
  };

  React.useEffect(() => {
    // Clear the timer if the component is unmounted
    return () => clearTimeout(searchTypingTimer);
  }, [searchTypingTimer]);

  React.useEffect(() => {
    setFilteredPatients(advancedSearch.data);
  }, [advancedSearch.data]);

  const handlePatientFiltering = (value: FilterType) => {
    const searchValues = pickBy(search => Boolean(search && !isEmpty(search)), value) as FilterType;

    if (isEmpty(searchValues)) {
      setFilteredPatients(undefined);
      return;
    }

    const fPatients = patients.filter(fndPatient => {
      let isMatch = true;

      if (searchValues.name) {
        isMatch =
          isMatch &&
          (`${fndPatient.firstName} ${fndPatient.lastName}`
            .toLowerCase()
            .includes(searchValues.name.toLowerCase()) ||
            `${fndPatient.lastName} ${fndPatient.firstName}`
              .toLowerCase()
              .includes(searchValues.name.toLowerCase()));
      }

      if (searchValues.state) {
        isMatch = isMatch && fndPatient.state === searchValues.state;
      }

      if (searchValues.gender) {
        isMatch = isMatch && fndPatient.patientDetails.gender === searchValues.gender;
      }

      if (searchValues.provider) {
        isMatch =
          isMatch &&
          (`${fndPatient?.assignedProvider?.firstName} ${fndPatient?.assignedProvider?.lastName}`
            .toLowerCase()
            .includes(searchValues.provider.toLowerCase()) ||
            `${fndPatient?.assignedProvider?.lastName} ${fndPatient?.assignedProvider?.firstName}`
              .toLowerCase()
              .includes(searchValues.provider.toLowerCase()));
      }

      return isMatch;
    });

    if (fPatients && fPatients.length > 0) {
      setFilteredPatients(fPatients);
      return;
    }

    setFilteredPatients([]);

    clearTimeout(doneSearchTypingInterval);

    const newTimer = setTimeout(() => {
      setExpandedSearch({
        q: searchValues?.name || '',
        // q: Object.values(searchValues)
        //   .filter(sValue => sValue)
        //   .join('|'),
        state: searchValues?.state,
        gender: searchValues?.gender,
      }); // TODO: take a look at this filter again
    }, doneSearchTypingInterval);

    setSearchTypingTimer(newTimer);
  };

  const handlePatientClicked = (patient: PatientType) => {
    if (!find(propEq('id', patient.id))(patientsWithVisits)) {
      sessionTStorage.set(TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY, JSON.stringify(patient));
    }

    dispatch({
      type: 'SET_ASSIGNED_PATIENTS',
      payload: uniqBy(prop('id'), [...patientsWithVisits, patient]),
    });
    navigate(`${paths.PATIENT_URL_PATH.replace(':patientId', patient.id)}`);
  };

  const handleVisitClicked = (visit: QuestionAnswers) => {
    dispatch({ type: 'SET_ASSIGNED_PATIENTS', payload: patientsWithVisits });

    const url = new URL(
      `${window.location.origin}${paths.VISIT_RECORDS_URL_PATH.replace(':visitId', visit.id)}`,
    );

    url.searchParams.append('type', visit.type.toLocaleLowerCase());

    url.searchParams.append('patient', visit.patientUserId);

    if (hasUnReadAnsweredQnAs(visit) || hasANewFollowUp(visit) || hasANewLabReport(visit)) {
      url.pathname = hasANewFollowUp(visit)
        ? `${url.pathname}/treatment-plan`
        : hasUnReadAnsweredQnAs(visit)
        ? `${url.pathname}/chat`
        : `${url.pathname}/labs`;

      url.hash = hasANewFollowUp(visit) ? '#follow-ups' : '';

      if (hasUnReadAnsweredQnAs(visit)) {
        const QnAsId = unReadAnsweredQnAs?.find(
          fndUnReadQnAs => fndUnReadQnAs?.visitId === visit.id,
        )?.QnAs?.id;

        if (QnAsId) {
          url.searchParams.append('QnAsId', QnAsId);
        }
      }

      const { queryKey } = patientsQuestionnairesQuery(patients, user?.id);
      queryClient.invalidateQueries({ queryKey });
    }

    navigate(url.toString().replace(window.location.origin, ''));
  };

  const getVisitTitle = (type: string) => {
    const findVisit = visitTypes.find(fndVisit => fndVisit.type === type.toLocaleUpperCase());
    if (!findVisit) return type;
    return [
      VISIT_TYPE_CONSTANTS.SICK_VISITS,
      VISIT_TYPE_CONSTANTS.UTI_VISITS,
      VISIT_TYPE_CONSTANTS.INTRO,
    ].includes(findVisit.type)
      ? `${findVisit.name} Visit`
      : findVisit.name;
  };

  const getVisitPatientName = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return `${findPatient?.firstName} ${findPatient?.lastName}`;
  };

  const getVisitPatientSignedAvatar = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return findPatient?.profilePicture || '';
  };

  const getVisitPatientDOBAndGender = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return findPatient ? formatPatientAgeAndGender(findPatient) : '-';
  };

  const getVisitPatientState = (patientId: string) => {
    const findPatient = patients.find(fndPatient => fndPatient.id === patientId);
    return US_STATES.find(state => state.value === findPatient?.state)?.label || '-';
  };

  const hasANewLabReport = (visit: QuestionAnswers) => {
    return visit.labTests && !hasOpenedLabReport(visit.labTests);
  };

  const hasANewFollowUp = (visit: QuestionAnswers) => {
    return (
      visit.status !== VISIT_STATUS.COMPLETE &&
      Boolean(visit.followUps) &&
      !hasOpenedFollowUp(visit.followUps)
    );
  };

  const visitColumnFloor = (() => {
    const widest = visits.reduce((most, visit) => Math.max(most, getVisitEvents(visit).length), 0);

    if (widest >= 3) return 400;
    if (widest === 2) return 340;

    return 288;
  })();

  const getPendingVisitTitle = (visit: QuestionAnswers) => getVisitTitle(visit.type);

  if (queryParams.get('photon-redirect')) {
    return <Navigate to={queryParams.get('photon-redirect') || paths.HOME_URL_PATH} replace />;
  }

  return (
    <div className="relative h-full flex flex-col justify-center items-center pt-4">
      <div className="text-center tablet:text-center">
        <h1 className="font-semibold text-[25px] tablet:text-[30px] text-river-black">
          Welcome, {user?.firstName || 'Doc'}
        </h1>
      </div>

      <div
        className="relative pt-10 pb-5 w-full space-y-8 laptop:pt-8 laptop:pb-8 laptop:w-[92%] desktop:w-3/4 mx-auto laptop:space-y-10 
          px-4 tablet:px-10 desktop:px-0"
      >
        <div className="flex flex-col">
          <div className="flex flex-row justify-between mb-5">
            <div className="text-neutral-500 text-xs uppercase font-medium">
              {isLoading && isFetching ? (
                <Skeleton width={125} height={24} className="block rounded-full" duration={1.4} />
              ) : !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? (
                'PENDING VISITS'
              ) : (
                'VISITS'
              )}
            </div>

            {isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? (
              <button
                onClick={() =>
                  navigate(
                    `${paths.VISITS_URL_PATH}?status=${
                      !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? 'pending' : 'complete'
                    }`,
                  )
                }
                type="button"
                disabled={visits.length === 0}
                className="text-river-blue inline-flex items-center text-xs font-medium disabled:opacity-50"
              >
                {isLoading && isFetching ? (
                  <Skeleton width={90} height={24} className="block rounded-full" duration={1.4} />
                ) : (
                  'VIEW ALL'
                )}
              </button>
            ) : null}
          </div>

          <div className="divide-y divide-[#EDF0F3]">
            {isLoading && isFetching ? (
              <div className="w-full flex flex-col space-y-4">
                {new Array(4).fill(null).map((_, index) => (
                  <Skeleton
                    key={index}
                    width="100%"
                    height={53}
                    className="block rounded-full"
                    duration={1.4}
                  />
                ))}
              </div>
            ) : visits.length === 0 ? (
              <div className="w-full flex flex-col justify-center items-center">
                <Graphic id="graphicNoPendingVisit" className="h-[120px] tablet:h-[130px]" />
                <div className="text-center mt-3">
                  <p className="font-medium">You’re all caught up on your visits</p>
                  <p className="text-neutral-500 text-xs">See all pending visits in one place</p>
                </div>
              </div>
            ) : (
              visits
                .slice(0, isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN') ? 5 : undefined)
                .map(visit => (
                  <div
                    key={visit.id}
                    role="presentation"
                    onClick={() => {
                      rememberVisitOpened(visit);
                      handleVisitClicked(visit);
                    }}
                    className="flex cursor-pointer relative justify-between items-center laptop:grid
                      py-[10px] pl-10 pr-4 hover:bg-[#F1F6FC]"
                    style={{
                      gridTemplateColumns: `minmax(${visitColumnFloor}px, 0.9fr) minmax(360px, 1fr)`,
                    }}
                  >
                    {/* Three fixed slots; a lone dot takes the middle. */}
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-[2px]">
                      {(() => {
                        const events = getVisitEvents(visit);
                        const leading = events.length === 1 ? 1 : 0;
                        const slots: Array<VisitNotificationEvent | null> = [null, null, null];

                        events.forEach((eventKey, position) => {
                          slots[leading + position] = eventKey;
                        });

                        return slots.map((eventKey, slot) => (
                          <span
                            // eslint-disable-next-line react/no-array-index-key
                            key={slot}
                            className="w-[8px] h-[8px] shrink-0 flex items-center justify-center"
                          >
                            {eventKey && (
                              <span
                                style={{
                                  backgroundColor: EVENT_STYLES[eventKey].dot,
                                  width: events.length >= 3 ? 6 : 8,
                                  height: 8,
                                }}
                                title={EVENT_STYLES[eventKey].label}
                                className="rounded-full"
                              />
                            )}
                          </span>
                        ));
                      })()}
                    </div>

                    <div className="flex items-center space-x-6 w-full min-w-0">
                      <div
                        style={{ backgroundColor: getDiscStyle(visit).tint }}
                        className="w-[50px] h-[50px] rounded-full flex items-center justify-center shrink-0"
                      >
                        {/* These assets hardcode a per-type fill, so `color`
                            alone never reaches them. */}
                        <Icon
                          className={`icon-visit icon-${visit.type.toLocaleLowerCase()}-visit [&_path]:fill-current [&_path]:stroke-current`}
                          id={visitIconsAndColors[visit.type.toLocaleLowerCase()]?.icon || ''}
                          style={{ color: getDiscStyle(visit).dot }}
                        />
                      </div>
                      <div className="w-full flex flex-col min-w-0">
                        <div className="flex flex-row items-center gap-2 min-w-0 laptop:flex-col laptop:items-start laptop:gap-0">
                          <div className={`truncate ${isRowBold(visit) ? 'font-semibold' : ''}`}>
                            {getPendingVisitTitle(visit)}
                          </div>
                          {getVisitEvents(visit).length > 0 && (
                            <div className="flex gap-[5px] shrink-0 laptop:mt-[3px]">
                              {getVisitEvents(visit).map(eventKey => (
                                <span
                                  key={eventKey}
                                  style={{
                                    backgroundColor: EVENT_STYLES[eventKey].tint,
                                    color: EVENT_STYLES[eventKey].dot,
                                  }}
                                  className="inline-flex items-center rounded-full px-[9px] py-0 text-[10.5px] leading-[16px] font-semibold whitespace-nowrap"
                                >
                                  {EVENT_STYLES[eventKey].label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-flex justify-between w-full laptop:hidden items-baseline">
                          <div className="text-xs text-[#5F6B76] inline-flex truncate">
                            {getVisitPatientName(visit.patientUserId)}
                            <span className="ml-1">
                              ({getVisitPatientDOBAndGender(visit.patientUserId)})
                            </span>
                          </div>

                          <div className="items-center flex">
                            <Icon
                              id="iconArrowForward"
                              className="w-4 text-[#737373] group-hover:text-river-black"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="w-full hidden laptop:flex items-center gap-4 min-w-0">
                      <div className="flex-1 min-w-0">
                        <Patient
                          key={visit.patientUserId}
                          avatar={getVisitPatientSignedAvatar(visit.patientUserId)}
                          avatarBgColor="#DFE5E7"
                          name={getVisitPatientName(visit.patientUserId)}
                          about={getVisitPatientDOBAndGender(visit.patientUserId)}
                          state={getVisitPatientState(visit.patientUserId)}
                          onPatientClick={() => false}
                          isLoadingPatient={false}
                          showArrowNext
                          condensed
                        />
                      </div>

                      {(() => {
                        const stamp = visitDateStamp(visit, hasUnReadAnsweredQnAs(visit));

                        if (!stamp)
                          return <div className="hidden desktop:block w-[92px] shrink-0" />;

                        return (
                          <div className="hidden desktop:flex w-[92px] shrink-0 flex-col items-end leading-tight">
                            <span className="text-[10px] uppercase tracking-[.06em] text-[#97A2AC] whitespace-nowrap">
                              {stamp.caption}
                            </span>
                            <span className="text-xs text-[#5F6B76] tabular-nums">
                              {formatStampDate(stamp.at)}
                            </span>
                          </div>
                        );
                      })()}

                      <Icon
                        id="iconArrowForward"
                        className="w-4 shrink-0 text-[#737373] group-hover:text-river-black"
                      />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        <hr className="block border-b-[1px] border-t-0 border-[#e9ecef]" />

        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center mb-5">
            <div className="text-neutral-500 text-xs uppercase font-medium shrink-0">
              PATIENT PANEL
            </div>

            <div className="relative w-full tablet:w-[80%] flex flex-row justify-end">
              <motion.div
                layout
                initial={false}
                animate={false}
                variants={{
                  open: { opacity: 1, display: 'block', position: 'absolute' },
                  closed: { opacity: 0, display: 'none' },
                }}
                transition={{ duration: 0 }}
                className="w-full"
              >
                <SearchFilter
                  fields={
                    user && !isRole(user?.roles || [], 'COLLABORATING-PHYSICIAN')
                      ? ['name', 'gender', 'state']
                      : ['name', 'state', 'provider']
                  }
                  onSearchViewClose={() => false}
                  onFilter={handlePatientFiltering}
                />
              </motion.div>
            </div>
          </div>

          <div className="space-y-5">
            {isEmpty(patientsWithVisits) && patientsWithVisits.length === 0 ? (
              <div className="w-full flex flex-col justify-center items-center">
                <Graphic id="graphicNoPatients" className="h-[160px]" />
                <div className="text-center mt-2">
                  <p className="font-medium">All clear! No patients assigned.</p>
                  <p className="text-[#7A7A7A] text-xs">View assignments in one place.</p>
                </div>
              </div>
            ) : isEmpty(filteredPatients) ? (
              <div className="w-full flex flex-col justify-center items-center mt-10">
                {advancedSearch.isFetching ? (
                  <>
                    <Icon id="loader" className="w-8 h-8 self-center" />

                    <div className="text-center self-center mt-2">
                      <p className="font-medium">Please wait...</p>
                      <p className="text-[#7A7A7A] text-xs">
                        Performing a wider search for more results.
                      </p>
                    </div>
                  </>
                ) : advancedSearch.status === 'error' ? (
                  <div className="text-center self-center mt-2">
                    <p className="font-medium">No results found.</p>
                    <p className="text-[#7A7A7A] text-xs">Search for more results failed.</p>
                  </div>
                ) : null}
              </div>
            ) : (
              (filteredPatients || patientsWithVisits)
                .slice(0, patientsLimit)
                .map((patient: PatientType) => (
                  <Patient
                    key={patient.id}
                    avatar={patient.profilePicture}
                    avatarBgColor="#DFE5E7"
                    name={`${patient.firstName} ${patient.lastName}`}
                    about={formatPatientAgeAndGender(patient)}
                    state={US_STATES.find(state => state.value === patient.state)?.label || '-'}
                    onPatientClick={() => handlePatientClicked(patient)}
                    isLoadingPatient={false}
                  />
                ))
            )}
          </div>

          {patientsWithVisits.length > PATIENTS_LIMIT &&
            patientsLimit === PATIENTS_LIMIT &&
            !filteredPatients && (
              <div className="text-center pb-8 text-river-blue mt-4">
                <button
                  onClick={() => setPatientsLimit(patientsWithVisits.length)}
                  type="button"
                  className="p-2 !font-medium"
                >
                  Show more patients...
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

export default Home;
